import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";
import { Loader2, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuthedHttp, errMsg, useGet, waLink, inr } from "@/lib/api";
import { useShop, useSeo } from "@/lib/store";
import { useQuote, Summary, CouponBox } from "./Cart";

const loadRzp = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  useSeo("Checkout");
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const nav = useNavigate();
  const call = useAuthedHttp();
  const { cart, coupon, clear, settings } = useShop();
  const { q, err } = useQuote();
  const { data: pay } = useGet("/payments/config");
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState({ name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "Uttar Pradesh", pincode: "" });
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  useEffect(() => { if (userEmail) setA((s) => (s.email ? s : { ...s, email: userEmail })); }, [userEmail]);

  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  if (!cart.length) return <Navigate to="/cart" replace />;

  const place = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const items = cart.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty }));
      const address = { ...a, email: a.email || user?.primaryEmailAddress?.emailAddress };
      const o = await call("post", "/orders", { items, coupon, address });
      if (!(await loadRzp())) throw new Error("Could not load Razorpay checkout. Check your connection.");
      const rz = new window.Razorpay({
        key: o.key_id, amount: o.amount, currency: o.currency, order_id: o.razorpay_order_id, name: "DEN & THREADS", description: `Order ${o.order_no}`,
        prefill: { name: address.name, email: address.email, contact: address.phone }, theme: { color: "#C6FF00" },
        handler: async (resp) => {
          try {
            await call("post", `/orders/${o.order_id}/verify`, resp);
            clear();
            nav(`/orders/${o.order_id}?placed=1`);
          } catch (er) { toast.error(errMsg(er)); nav(`/orders/${o.order_id}`); }
        },
        modal: { ondismiss: () => { setBusy(false); toast("Payment cancelled — your cart is saved."); } },
      });
      rz.on("payment.failed", async () => { await call("post", `/orders/${o.order_id}/failed`).catch(() => {}); toast.error("Payment failed. No money was captured — try again."); setBusy(false); });
      rz.open();
    } catch (er) { toast.error(errMsg(er)); setBusy(false); }
  };

  const F = (k, l, p = {}) => <label className={p.wide ? "sm:col-span-2" : ""}><span className="label">{l}</span><input required={!p.opt} className="field" value={a[k]} onChange={set(k)} placeholder={p.ph} data-testid={`address-${k}-input`} type={p.type || "text"} /></label>;

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-12" data-testid="checkout-page">
      <h1 className="font-display text-5xl sm:text-6xl">CHECKOUT</h1>
      {pay && !pay.enabled && (
        <div className="mt-6 border border-yellow-500/50 bg-yellow-500/10 p-4 flex gap-3 text-sm" data-testid="payments-disabled-banner">
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>Online payments are being set up. You can still place your order on WhatsApp — <a className="underline text-neon" href={waLink(settings.whatsapp, `Hi, I want to order: ${cart.map((c) => `${c.title} (${c.size}) x${c.qty}`).join(", ")}${q ? ` — total ${inr(q.total)}` : ""}`)} target="_blank" rel="noreferrer" data-testid="whatsapp-order-link">order via WhatsApp</a>.</div>
        </div>
      )}
      <form onSubmit={place} className="grid lg:grid-cols-[1.4fr_1fr] gap-10 mt-10">
        <div>
          <p className="eyebrow mb-5">Shipping address</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {F("name", "Full name")}{F("phone", "Mobile (10 digits)", { ph: "9876543210" })}
            {F("email", "Email", { type: "email", ph: user?.primaryEmailAddress?.emailAddress, opt: true, wide: true })}
            {F("line1", "Address line 1", { wide: true })}{F("line2", "Address line 2 (optional)", { wide: true, opt: true })}
            {F("city", "City")}{F("state", "State")}{F("pincode", "PIN code", { ph: "208001" })}
          </div>
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 self-start">
          <div className="bg-[#171717] p-5 space-y-2 text-sm max-h-60 overflow-auto">
            {cart.map((i) => <div key={i.key} className="flex justify-between gap-2"><span className="truncate">{i.title} · {i.size} × {i.qty}</span><span className="font-mono">{inr(i.price * i.qty)}</span></div>)}
          </div>
          <CouponBox q={q} />
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <Summary q={q}>
            <button disabled={busy || !q || (pay && !pay.enabled)} className="btn-neon w-full mt-4" data-testid="place-order-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}Pay securely with Razorpay
            </button>
            <p className="text-[11px] text-grey">UPI · Cards · Netbanking · Wallets. Payment is verified on our server before your order is confirmed.</p>
          </Summary>
          <Link to="/cart" className="text-xs text-grey underline">← Back to bag</Link>
        </div>
      </form>
    </div>
  );
}
